import { useEffect, useRef } from 'react';
import { clearDraft, serializeDraft, writeDraftJson } from './draftStorage';

/** Long enough that a typist writes one entry, short enough to survive a crash. */
const DRAFT_DEBOUNCE_MS = 500;

/**
 * Mirrors a form's in-progress value into `localStorage`, debounced.
 *
 * `pristine` is the value the form would hold with no draft applied — the empty
 * form, or the record as the server has it. While the two match there is
 * nothing to recover, so the stored draft is cleared rather than rewritten;
 * that is also what retires a draft the moment a save lands, since the saved
 * record becomes the new pristine value.
 *
 * A null `key` disables the hook, for the render or two before a user or
 * business id is known.
 *
 * Restoring is deliberately not part of this hook. Callers read the draft in a
 * lazy `useState` initialiser (`restoreDraft`), so the recovered work is on
 * screen from the first render and no effect writes state synchronously.
 */
export function useDraft<T>(
  key: string | null,
  value: T,
  pristine: T,
  delayMs: number = DRAFT_DEBOUNCE_MS,
): void {
  const serialized = serializeDraft(value);
  const pristineSerialized = serializeDraft(pristine);
  // Seeded with the value at mount, so a just-restored draft is not immediately
  // rewritten with a fresh timestamp.
  const lastWritten = useRef<string | null>(serialized);

  useEffect(() => {
    if (key === null || serialized === null) return;

    if (serialized === pristineSerialized) {
      clearDraft(key);
      lastWritten.current = null;
      return;
    }

    if (serialized === lastWritten.current) return;

    const timer = setTimeout(() => {
      writeDraftJson(key, serialized);
      lastWritten.current = serialized;
    }, delayMs);

    return () => clearTimeout(timer);
  }, [key, serialized, pristineSerialized, delayMs]);
}
